class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.totalObserver = new MutationObserver(() => this.queueDulnairTotals());
    this.totalObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(
      '#cart-icon-bubble, header a[href$="/cart"], .shopify-section-group-header-group a[href$="/cart"]'
    );

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() !== 'SPACE') return;
        event.preventDefault();
        this.open(cartLink);
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.queueDulnairTotals();
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  queueDulnairTotals() {
    cancelAnimationFrame(this.totalFrame);
    this.totalFrame = requestAnimationFrame(() => this.syncDulnairTotals());
  }

  syncDulnairTotals() {
    const source = this.querySelector('.totals__total-value');
    const heading = this.querySelector('.drawer__heading');
    if (!source || !heading) return;

    const total = source.textContent.trim();
    let headingTotal = heading.querySelector('.dulnair-cart-total--heading');
    if (!headingTotal) {
      headingTotal = document.createElement('span');
      headingTotal.className = 'dulnair-cart-total--heading';
      heading.append(headingTotal);
    }

    const checkout = this.findDulnairCheckoutButton();
    if (!checkout) {
      if (headingTotal.textContent !== `(${total})`) headingTotal.textContent = `(${total})`;
      return;
    }

    checkout.style.setProperty('background-color', '#000b2b', 'important');
    checkout.style.setProperty('background-image', 'none', 'important');
    checkout.style.setProperty('border-color', '#000b2b', 'important');
    checkout.style.setProperty('color', '#fffdf9', 'important');

    let buttonTotal = checkout.querySelector('.dulnair-cart-total--button');
    if (!buttonTotal) {
      buttonTotal = document.createElement('span');
      buttonTotal.className = 'dulnair-cart-total--button';
      checkout.append(buttonTotal);
    }

    if (headingTotal.textContent !== `(${total})`) headingTotal.textContent = `(${total})`;
    if (buttonTotal.textContent !== total) buttonTotal.textContent = total;
  }

  findDulnairCheckoutButton() {
    const candidates = Array.from(this.querySelectorAll('.drawer__footer button, .drawer__footer a, .drawer__footer [role="button"]'));
    return (
      candidates.find((element) => /buy now/i.test(element.textContent)) ||
      this.querySelector('#CartDrawer-Checkout, .cart__checkout-button, .drawer__footer [name="checkout"]')
    );
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.queueDulnairTotals();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
